import React, { useMemo, useState, useEffect, useRef } from 'react';
import './MetricCard.css';

// ─── Palette unique par tone ───────────────────────────────────────────────
const TONE = {
  success: {
    cardBg: 'rgba(175, 207, 167, 0.38)',
    glow:   'rgba(134, 187, 124, 0.35)',
    iconBg: 'rgba(220, 252, 231, 0.80)',
    border: 'rgba(134, 187, 124, 0.30)',
  },
  danger: {
    cardBg: 'rgba(250, 190, 190, 0.42)',
    glow:   'rgba(239, 120, 120, 0.32)',
    iconBg: 'rgba(255, 228, 228, 0.82)',
    border: 'rgba(239, 120, 120, 0.28)',
  },
  warning: {
    cardBg: 'rgba(254, 215, 130, 0.42)',
    glow:   'rgba(245, 158, 11,  0.30)',
    iconBg: 'rgba(255, 243, 195, 0.82)',
    border: 'rgba(245, 158, 11,  0.28)',
  },
  low: {
    cardBg: 'rgba(71, 176, 222, 0.22)',
    glow:   'rgba(47, 117, 148,  0.30)',
    iconBg: 'rgba(219, 234, 254, 0.82)',
    border: 'rgba(47, 117, 148,  0.25)',
  },
  neutral: {
    cardBg: 'rgba(255, 255, 255, 0.55)',
    glow:   'rgba(100, 116, 139, 0.15)',
    iconBg: 'rgba(241, 245, 249, 0.80)',
    border: 'rgba(200, 210, 220, 0.35)',
  },
};

const BADGE = {
  Abnormal: { bg: 'rgba(197, 151, 34, 0.18)', color: '#b45309' },
  Normal:   { bg: 'rgba(34,  197, 94, 0.22)', color: '#15803d' },
  High:     { bg: 'rgba(197, 34,  34, 0.18)', color: '#b91c1c' },
  Low:      { bg: 'rgba(59,  130, 246, 0.18)', color: '#1d4ed8' },
};

const stripUnits = (raw = '') => String(raw).replace(/[^\d./]/g, '').trim();

// ─── Component ─────────────────────────────────────────────────────────────
const MetricCard = ({
  value,
  unit,
  label,
  status,
  tone             = 'neutral',
  iconsrc,
  circleBg,
  unitBold         = false,
  unitLarge        = false,
  editable         = false,
  clickToEdit      = false,
  onValueChange,
  inputPlaceholder,
  intervals        = null, // { label: { min, max, badgeLabel }[] }
  onEditStart,
  onEditEnd,
}) => {
  const c  = TONE[tone] || TONE.neutral;

  // Détermination dynamique du status si intervals fourni et status non fourni
  let computedStatus = status;
  if (!status && intervals && Array.isArray(intervals)) {
    const numericValue = parseFloat(stripUnits(value));
    for (const interval of intervals) {
      if (
        (interval.min === undefined || numericValue >= interval.min) &&
        (interval.max === undefined || numericValue <= interval.max)
      ) {
        computedStatus = interval.badgeLabel;
        break;
      }
    }
  }
  const bs = BADGE[computedStatus] || BADGE[status];

  // ✅ Track whether the user is actively editing
  // When focused, we don't sync from props (so typing isn't interrupted)
  // When not focused, we sync from props (so external updates are reflected)
  const isFocused = useRef(false);
  const inputRef = useRef(null);
  const [localEditMode, setLocalEditMode] = useState(false);
  const [editValue, setEditValue] = useState(() => stripUnits(value));

  const isInEditMode = editable || localEditMode;

  // ✅ Only sync from props when the input is NOT focused
  useEffect(() => {
    if (!isFocused.current) {
      setEditValue(stripUnits(value));
    }
  }, [value]);

  // ✅ Also reset when editable switches off → on (new edit session starts)
  const prevEditable = useRef(editable);
  useEffect(() => {
    if (editable && !prevEditable.current) {
      setEditValue(stripUnits(value));
    }
    prevEditable.current = editable;
  }, [editable, value]);

  useEffect(() => {
    if (clickToEdit && localEditMode && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select?.();
    }
  }, [clickToEdit, localEditMode]);

  useEffect(() => {
    if (!localEditMode && !editable && clickToEdit) {
      onEditEnd?.();
    }
  }, [clickToEdit, localEditMode, editable, onEditEnd]);

  const handleCardClick = () => {
    if (!clickToEdit || editable || localEditMode) return;
    setLocalEditMode(true);
    onEditStart?.();
  };

  const handleChange = (e) => {
    const stripped = stripUnits(e.target.value);
    setEditValue(stripped);
    // ✅ Fire immediately on every keystroke so tempMetrics always stays in sync
    if (onValueChange) onValueChange(stripped);
  };

  const handleFocus = () => { isFocused.current = true; };
  const handleBlur  = () => {
    isFocused.current = false;
    if (clickToEdit && localEditMode) {
      setLocalEditMode(false);
      onEditEnd?.();
    }
  };

  return (
    <article
      className="mc"
      role="group"
      aria-label={label}
      onClick={handleCardClick}
      style={{
        '--mc-card-bg':     c.cardBg,
        '--mc-card-glow':   c.glow,
        '--mc-card-border': c.border,
      }}
    >
      {/* Shine overlay */}
      <div className="mc-shine" aria-hidden />

      {/* Icône */}
      <div
        className="mc-icon"
        style={{ backgroundColor: circleBg || c.iconBg }}
        aria-hidden
      >
        {iconsrc ? (
          <img src={iconsrc} alt="" width={22} height={22} />
        ) : (
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
            <circle cx="12" cy="12" r="9" stroke="#cbd5e1" strokeWidth="1.5" />
            <path d="M6 12h4l2-4 2 8 2-4h2" stroke="#64748b" strokeWidth="1.5" fill="none" />
          </svg>
        )}
      </div>

      {/* Contenu */}
      <div className="mc-main">
        <div className="mc-value">
          {isInEditMode ? (
            <div className="mc-edit-wrap">
              <input
                ref={inputRef}
                type="text"
                className="mc-input"
                value={editValue}
                placeholder={inputPlaceholder}
                onChange={handleChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                aria-label={`${label} value`}
              />
              {unit && (
                <span className={`unit ${unitBold ? 'unit--bold' : ''} ${unitLarge ? 'unit--lg' : ''}`}>
                  {unit}
                </span>
              )}
            </div>
          ) : (
            <>
              <span className="num">{value}</span>
              {unit && (
                <span className={`unit ${unitBold ? 'unit--bold' : ''} ${unitLarge ? 'unit--lg' : ''}`}>
                  {unit}
                </span>
              )}
            </>
          )}
        </div>

        <div className="mc-label">{label}</div>

        <span
          className="mc-badge"
          style={bs ? { background: bs.bg, color: bs.color } : undefined}
        >
          {computedStatus || status}
        </span>
      </div>
    </article>
  );
};

export default MetricCard;